/**
 * Which project's registry holds a comment, or the document it is about
 * (RFC 0301 S11).
 *
 * A comment is stored on a project's registry document, not on the page it
 * is about, and a session's tools are built against the session's HOME
 * registry (`tool-set.ts`). Those two came apart the first time a comment was
 * routed by space rather than by pairing (S10): a review page in one project,
 * the space's session in another, and the session woken for a comment its
 * tool could not find. "No comment <id> to reply to", on a comment it had
 * just been handed.
 *
 * The session already knows the page: it is holding it. The World lists every
 * project's registry. So the registry is RESOLVED from what the tool is acting
 * on, and nothing has to carry an id around. Home first, because it is the
 * answer nearly every time and costs no second document; then each registry
 * the World names. A miss everywhere returns home, so a create on a document
 * nobody lists lands where it always did.
 *
 * RFC 0336 S1 widened it from comments to every tool that acts on an id it
 * was handed: a document's edit check, a task, a session to message. An id
 * decides where a tool reads and writes, never the caller's project.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { projectString } from '../../model/project/scalars'
import type { ProjectDoc } from '../../model/project/types'
import { projectWorldProjects } from '../../model/world/reads'
import type { WorldDoc } from '../../model/world/types'
export interface RegistryQuery {
  /** A comment id: the registry whose `commentIndex` (or, before the move,
   *  `comments` map) has it. */
  commentId?: string
  /** A document id: the registry whose `documents` map lists it. */
  documentId?: string
  /** A session id: the registry whose `sessionIds` lists it (RFC 0336 S1). */
  sessionId?: string
  /** A task id: the registry whose `tasks` map has it (RFC 0336 S1). */
  taskId?: string
}

function holds(doc: unknown, q: RegistryQuery): boolean {
  const d = doc as ProjectDoc | undefined
  if (!d) return false
  // A comment is listed in the index of the project it is about (RFC 0339
  // 3d), or still on the project's leftovers if it has not moved.
  if (
    q.commentId &&
    (d.commentIndex?.[q.commentId] || d.comments?.[q.commentId])
  ) {
    return true
  }
  if (q.documentId && d.documents?.[q.documentId]) return true
  if (
    q.sessionId &&
    (d.sessionIds ?? []).some((id) => projectString(id) === q.sessionId)
  )
    return true
  if (q.taskId && d.tasks?.[q.taskId]) return true
  return false
}

async function registry(
  repo: Repo,
  id: string,
  timeoutMs: number,
): Promise<unknown> {
  try {
    // One deadline for the find AND the wait for it to be ready: a handle that
    // is found but never becomes ready must not hold a tool call open.
    const read = (async () => {
      const handle = await repo.find<ProjectDoc>(id as AnyDocumentId)
      await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
      return handle.doc()
    })()
    return await Promise.race([
      read,
      new Promise<undefined>((r) => setTimeout(() => r(undefined), timeoutMs)),
    ])
  } catch {
    // A registry the relay has never heard of, or one that is not synced
    // here. Not this one, then.
    return undefined
  }
}

/**
 * The registry id to act in for `q`: the home registry when it holds the
 * thing, else the first of the World's registries that does, else home.
 */
export async function registryHolding(
  repo: Repo,
  home: string,
  worldId: string | null | undefined,
  q: RegistryQuery,
  opts: { timeoutMs?: number } = {},
): Promise<string> {
  if (!q.commentId && !q.documentId && !q.sessionId && !q.taskId) return home
  const timeoutMs = opts.timeoutMs ?? 2_000
  // A document names its own project (DocumentDoc.workspaceId), so it is
  // read there rather than searched for. Checked against that project's
  // list, so a stale or forged field does not send a write somewhere else.
  if (q.documentId) {
    const own = (await registry(repo, q.documentId, timeoutMs)) as
      | { workspaceId?: unknown }
      | undefined
    const named = own?.workspaceId ? String(own.workspaceId) : ''
    if (named && holds(await registry(repo, named, timeoutMs), q)) return named
  }
  if (holds(await registry(repo, home, timeoutMs), q)) return home
  if (!worldId) return home
  const world = await registry(repo, worldId, timeoutMs)
  // In PARALLEL, and done at the FIRST project that holds it: an id is
  // listed by one project, so waiting on the rest (and on the slowest of
  // them, up to the deadline) bought nothing. Only a miss waits for all.
  const others = projectWorldProjects(world as WorldDoc | undefined)
    .map((ref) => ref.projectId)
    .filter((id) => id !== home)
  if (others.length === 0) return home
  return new Promise<string>((resolve) => {
    let left = others.length
    for (const id of others) {
      void registry(repo, id, timeoutMs).then((doc) => {
        if (holds(doc, q)) resolve(id)
        else if (--left === 0) resolve(home)
      })
    }
  })
}
